import {
  CheckCircle2,
  Fingerprint,
  Globe,
  PenLine,
  Upload,
  CalendarClock,
  RotateCw,
  LayoutGrid,
} from "lucide-react";

/** WF setup source code → display + guidance (capture layer only — never shift_roster) */
export const ATTENDANCE_METHOD_META = {
  default_present: {
    label: "Default Present",
    short: "Auto-present on working days",
    description:
      "Employees are treated as present unless HR marks leave or absence. Best for small teams with minimal tracking.",
    icon: CheckCircle2,
    accent: "emerald",
    tips: ["Use Daily Attendance only for exceptions", "Fastest payroll with least data entry"],
  },
  manual_hr: {
    label: "Manual Entry",
    short: "HR marks attendance daily",
    description:
      "HR or managers enter Present, Absent, Leave, and Half-day in Daily Attendance. No devices required.",
    icon: PenLine,
    accent: "sky",
    tips: ["Open Attendance → Daily Attendance each day", "Ideal when you do not use biometric devices"],
  },
  biometric: {
    label: "Biometric",
    short: "Device punch integration",
    description:
      "Connect fingerprint or face readers. Punches sync automatically into payroll-ready attendance.",
    icon: Fingerprint,
    accent: "violet",
    tips: ["Register devices under Attendance → Devices", "Missing punches can be filled manually"],
  },
  browser_login: {
    label: "Website Login Entry",
    short: "Employees self mark-in",
    description:
      "Staff log in to the web portal and mark their own attendance each working day.",
    icon: Globe,
    accent: "indigo",
    tips: ["Employees use Attendance → Mark attendance", "HR can still override in Daily Attendance"],
  },
  excel_upload: {
    label: "Excel Upload",
    short: "Spreadsheet import",
    description:
      "Upload monthly attendance sheets. No live check-in — bulk import from Excel or CSV.",
    icon: Upload,
    accent: "amber",
    tips: ["Use Attendance → Import for uploads", "Good for outsourced attendance sheets"],
  },
};

/** Work schedule layer — separate from attendance capture source */
export const WORK_SCHEDULE_META = {
  fixed_schedule: {
    label: "Fixed Schedule",
    short: "Same timings for all working days",
    icon: CalendarClock,
    accent: "slate",
  },
  shift_roster: {
    label: "Shift Roster",
    short: "Per-day shift assignments via roster board",
    icon: LayoutGrid,
    accent: "violet",
  },
  rotational_shift: {
    label: "Rotational Shift",
    short: "Rotating shift patterns across teams",
    icon: RotateCw,
    accent: "indigo",
  },
  weekly_shift_pattern: {
    label: "Weekly Shift Pattern",
    short: "Repeating weekly shift cycle",
    icon: CalendarClock,
    accent: "sky",
  },
  open_shift: {
    label: "Open Shift",
    short: "Flexible shift without fixed roster grid",
    icon: LayoutGrid,
    accent: "amber",
  },
};

export const ROSTER_CONFIG_META = {
  weekly_roster: { label: "Weekly Roster" },
  monthly_roster: { label: "Monthly Roster" },
  rotational_roster: { label: "Rotational Roster" },
};

export function resolveCurrentSource(status = {}, hrAttendance = {}, profile = {}) {
  const fromProfile = profile?.default_source;
  if (fromProfile && fromProfile !== "shift_roster" && ATTENDANCE_METHOD_META[fromProfile]) {
    return fromProfile;
  }
  const progressSources = status?.setup_progress?.step_1?.sources;
  if (Array.isArray(progressSources) && progressSources.length === 1) {
    const s = progressSources[0];
    if (s !== "shift_roster") return s;
  }
  const modes = status?.enabled_modes || profile?.enabled_modes;
  if (Array.isArray(modes) && modes.length > 0) {
    const primary = modes.find((m) => m !== "shift_roster" && ATTENDANCE_METHOD_META[m]);
    if (primary) return primary;
    const fallback = modes.find((m) => m !== "shift_roster");
    if (fallback) return fallback;
  }
  const method = hrAttendance?.attendance_method;
  if (method === "manual") return "manual_hr";
  if (method === "web") return "browser_login";
  if (method && ATTENDANCE_METHOD_META[method]) return method;
  return "";
}

export function readWorkSchedule(profile = {}, hrAttendance = {}) {
  const p = profile || {};
  const att = hrAttendance || {};
  return {
    work_schedule_type:
      p.work_schedule_type || att.work_schedule_type || "fixed_schedule",
    enable_shift_roster: Boolean(
      p.enable_shift_roster ?? att.enable_shift_roster ?? false
    ),
    roster_config: p.roster_config || att.roster_config || [],
    work_schedule_label: p.work_schedule_label || null,
  };
}

export function readWorkingDays(org = {}, payrollSettings = {}) {
  const raw =
    org.monthly_salary_divisor_days ??
    payrollSettings.monthly_salary_divisor_days ??
    org.payroll?.monthly_salary_divisor_days;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 26;
}

/** Show shift management / roster nav when work schedule has roster enabled. */
export function isShiftAttendanceEnabled(profile = {}) {
  const p = profile || {};
  if (p.shift_features_enabled != null) {
    return Boolean(p.shift_features_enabled);
  }
  if (p.enable_shift_roster) return true;
  const ws = p.work_schedule_type || p.attendance_cycle_type;
  return ["shift_roster", "rotational_shift", "weekly_shift_pattern", "open_shift", "shift_based", "roster_based"].includes(ws);
}

export function formatWorkScheduleSummary(profile = {}) {
  const p = profile || {};
  if (p.work_schedule_label) return p.work_schedule_label;
  const ws = readWorkSchedule(p);
  if (ws.enable_shift_roster && ws.work_schedule_type === "fixed_schedule") {
    return "Shift Roster Enabled";
  }
  return WORK_SCHEDULE_META[ws.work_schedule_type]?.label || ws.work_schedule_type;
}

export function formatAttendanceSourceLabel(source, profile = {}) {
  const p = profile || {};
  if (p.attendance_source_label) return p.attendance_source_label;
  return ATTENDANCE_METHOD_META[source]?.label || source || "Not set";
}
